(() => {
  'use strict';
  if (customElements.get('dac-gifting')) return;
  const labels = { family: 'Family', friend: 'Friends', gift: 'Gift recipients' };
  const node = (tag, text, className) => {
    const element = document.createElement(tag);
    if (text != null) element.textContent = text;
    if (className) element.className = className;
    return element;
  };
  const addressText = recipient => [
    `${recipient.firstName} ${recipient.lastName}`,
    recipient.address.address1, recipient.address.address2,
    [recipient.address.city, recipient.address.province, recipient.address.postalCode].filter(Boolean).join(', '),
    recipient.address.countryName || recipient.address.countryCode,
  ].filter(Boolean).join('\n');

  class Gifting extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.copy = JSON.parse(this.querySelector('[data-gift-copy]').textContent);
      this.serviceConfigured = Boolean(this.dataset.apiBase);
      this.connecting = this.serviceConfigured;
      this.recipients = new Map();
      this.allocations = new Map();
      this.abort = new AbortController();
      this.dialog = this.querySelector('[data-gift-dialog]');
      this.form = this.querySelector('[data-recipient-form]');
      this.destination = this.querySelector('[data-gift-destination]');
      this.quantity = this.querySelector('[name="giftQuantity"]');
      this.status = this.querySelector('[data-gift-status]');
      this.error = this.querySelector('[data-gift-error]');
      const listen = (target, event, handler, options = {}) => target.addEventListener(event, handler, { ...options, signal: this.abort.signal });
      listen(document, 'click', event => this.click(event));
      listen(document, 'submit', event => this.checkout(event), { capture: true });
      listen(document, 'click', event => {
        if (event.target.closest('button[name="checkout"],input[name="checkout"],a[href*="/checkout"]')) this.checkout(event);
      }, { capture: true });
      listen(document, 'cart:sectionUpdated', () => {
        if (this.dialog.open && this.editing?.line) this.dialog.close();
        this.renderCart(true);
        if (this.serviceReady) this.persistPlan().catch(error => this.showError(error));
      });
      listen(this.destination, 'change', () => this.destinationChanged());
      listen(this.quantity, 'input', () => this.quantityHint());
      if (this.querySelector('[data-gift-notify]')) listen(this.querySelector('[data-gift-notify]'), 'change', () => this.notificationChanged());
      listen(this.form, 'invalid', event => { const details = event.target.closest('details'); if (details) details.open = true; }, { capture: true });
      listen(this.form.elements.countryCode, 'change', () => this.provinces());
      listen(this.form, 'submit', event => { event.preventDefault(); this.save(); });
      listen(this.dialog, 'close', () => { this.opener?.focus(); this.form.reset(); this.error.textContent = ''; });
      listen(window, 'pagehide', () => this.clearPrivateState());
      listen(window, 'pageshow', event => { this.renderCart(); this.renderRecipients(); if (event.persisted && this.serviceConfigured) this.connectService(); });
      this.querySelector('[data-recipient-add]')?.removeAttribute('hidden');
      this.provinces();
      this.renderCart();
      this.renderRecipients();
      if (this.serviceConfigured) this.connectService();
    }

    disconnectedCallback() { this.abort?.abort(); this.clearPrivateState(); this.ready = false; }

    clearPrivateState() {
      this.checkoutWatch?.abort(); this.pendingCheckout = null;
      this.dialog?.close(); this.reviewDialog?.close(); this.reviewDialog?.replaceChildren();
      this.notificationEmail = '';
      this.notificationsEnabled = false;
      this.api?.clear(); this.api = null; this.serviceReady = false; this.buyer = null;
      this.recipients?.clear(); this.allocations?.clear(); this.removedRecipient = null; this.buyerEdited = false; this.planVersion = 0; this.planWrite = null;
    }

    async click(event) {
      const edit = event.target.closest('[data-gift-edit]');
      if (edit && this.dataset.mode === 'cart') {
        const line = edit.closest('[data-gift-line]');
        this.open({ line, part: edit.dataset.part == null ? null : Number(edit.dataset.part) }, edit);
      }
      if (!this.contains(event.target)) return;
      if (event.target.closest('[data-gift-close],[data-gift-cancel]')) this.dialog.close();
      if (event.target.closest('[data-gift-save]')) this.save();
      const buyerEdit = event.target.closest('[data-gift-buyer-edit]');
      if (buyerEdit) this.open({ buyer: true, resumeCheckout: false }, buyerEdit);
      const undo = event.target.closest('[data-recipient-undo]');
      if (undo && this.removedRecipient) {
        undo.disabled = true;
        try {
          const person = this.serviceReady ? { ...await this.api.saveRecipient(this.removedRecipient), saved: true } : this.removedRecipient;
          this.recipients.set(person.id, person); this.removedRecipient = null;
          this.renderRecipients(); this.status.textContent = "Modtageren er gendannet.";
          this.focusRecipient(person.id);
        } catch (error) { undo.disabled = false; this.showError(error); }
      }
      if (event.target.closest('[data-gift-remove]')) {
        const parts = this.allocations.get(this.editing.line.dataset.lineKey) || [];
        parts.splice(this.editing.part, 1);
        this.dialog.close(); this.renderCart();
        this.opener = this.editing.line.querySelector('[data-gift-edit]'); this.opener?.focus();
        try { await this.persistPlan(); } catch (error) { this.showError(error); }
      }
      const add = event.target.closest('[data-recipient-add]');
      if (add) this.open({}, add);
      const recipientEdit = event.target.closest('[data-recipient-edit]');
      if (recipientEdit) this.open({ recipientId: recipientEdit.dataset.recipientEdit }, recipientEdit);
      const remove = event.target.closest('[data-recipient-remove]');
      if (remove) {
        if (this.serviceConfigured) {
          if (!this.serviceReady) return this.showError({ code: 'GIFTING_UNAVAILABLE' });
          const person = this.recipients.get(remove.dataset.recipientRemove);
          remove.disabled = true;
          try { await this.api.removeRecipient(person.id, person.version); }
          catch (error) { remove.disabled = false; return this.showError(error); }
        }
        this.removedRecipient = this.recipients.get(remove.dataset.recipientRemove);
        this.recipients.delete(remove.dataset.recipientRemove);
        this.renderRecipients();
        this.status.textContent = this.serviceReady ? "Modtageren er fjernet." : "Modtageren er fjernet fra denne forhåndsvisning.";
        const undoButton = node('button', 'Undo', 'dac-gifting__text'); undoButton.type = 'button'; undoButton.dataset.recipientUndo = ''; this.status.append(undoButton);
        this.querySelector('[data-recipient-add]').focus();
      }
    }

    open(editing, opener) {
      if (this.saving) return;
      this.editing = editing;
      this.opener = opener;
      this.form.reset(); this.error.textContent = '';
      const optional = this.querySelector('[data-gift-optional]');
      optional.open = Boolean(editing.recipientId || editing.buyer);
      optional.querySelector('summary').hidden = Boolean(editing.buyer);
      if (this.serviceConfigured && !this.serviceReady) return this.showError({ code: 'GIFTING_UNAVAILABLE' });
      const isCart = Boolean(editing.line);
      for (const field of this.form.elements) {
        if (!field.name) continue;
        if (field.dataset.wasRequired != null) { field.required = field.dataset.wasRequired === 'true'; delete field.dataset.wasRequired; }
        field.closest('.dac-gifting__field')?.removeAttribute('hidden');
      }
      this.form.elements.email.closest('.dac-gifting__field').hidden = isCart;
      this.querySelector('[data-gift-email-field]').hidden = !editing.buyer;
      if (this.querySelector('[data-gift-notification]')) this.querySelector('[data-gift-notification]').hidden = !isCart || !this.notificationsEnabled;
      this.form.elements.email.required = Boolean(editing.buyer && !this.signedIn);
      this.form.querySelector('label[for=gift-email]').textContent = editing.buyer ? "Din e-mailadresse" : "Modtagerens e-mailadresse (valgfrit)";
      this.form.elements.email.readOnly = Boolean(editing.buyer && this.signedIn);
      this.querySelector('[data-gift-email-hint]').textContent = editing.buyer ? this.copy.buyer_email_hint : this.copy.recipient_email_hint;
      if (editing.buyer) {
        for (const name of ['label', 'relationship', ...(editing.contactOnly ? ['countryCode', 'address1', 'address2', 'city', 'province', 'postalCode'] : [])]) {
          const field = this.form.elements[name];
          field.dataset.wasRequired = String(field.required); field.required = false;
          field.closest('.dac-gifting__field').hidden = true;
        }
      }
      this.querySelector('[data-gift-allocation]').hidden = !isCart;
      this.querySelector('[data-gift-save-label]').hidden = !isCart;
      this.querySelector('[data-gift-remove]').hidden = !isCart || editing.part == null;
      this.querySelector('#gift-dialog-title').textContent = isCart ? "Send som gave" : editing.buyer ? this.copy.buyer_title : editing.recipientId ? "Rediger modtager" : this.copy.add_recipient;
      this.querySelector('[data-gift-save]').textContent = isCart ? "Gem gaveoplysninger" : editing.buyer ? this.copy.buyer_save : "Gem modtager";
      this.querySelector('[data-gift-product]').textContent = editing.line?.dataset.title || "Din standardleveringsadresse er den samme.";
      this.destination.replaceChildren(new Option('My checkout address', 'buyer'));
      for (const person of this.recipients.values()) if (person.saved) this.destination.add(new Option(`${person.label} · ${labels[person.relationship]}`, person.id));
      this.destination.add(new Option('Someone else', 'new'));
      if (isCart) {
        const parts = this.allocations.get(editing.line.dataset.lineKey) || [];
        const part = editing.part == null ? null : parts[editing.part];
        const otherQuantity = parts.reduce((sum, current, index) => sum + (index === editing.part ? 0 : current.quantity), 0);
        this.quantity.max = Math.max(1, Number(editing.line.dataset.quantity) - otherQuantity);
        this.quantity.value = part?.quantity || 1;
        this.quantity.closest('.dac-gifting__field').hidden = Number(this.quantity.max) === 1;
        if (part && part.destinationId !== 'buyer' && ![...this.destination.options].some(option => option.value === part.destinationId)) {
          const person = this.recipients.get(part.destinationId);
          if (person) this.destination.add(new Option(person.label, person.id));
        }
        this.destination.value = part?.destinationId || 'new';
        this.destinationChanged(part?.notification);
      } else {
        this.form.hidden = false;
        this.querySelector('[data-gift-saved-summary]').hidden = true;
        const person = editing.buyer ? this.buyer : this.recipients.get(editing.recipientId);
        if (person) {
          for (const [key, value] of Object.entries({ ...person, ...person.address })) if (this.form.elements[key] && typeof value === 'string') this.form.elements[key].value = value;
          this.provinces(person.address?.province);
        } else this.provinces();
      }
      if (editing.buyer) {
        this.querySelector('#gift-dialog-title').textContent = editing.contactOnly ? "Hvem sender gaven?" : this.copy.buyer_title;
        this.querySelector('[data-gift-save]').textContent = editing.resumeCheckout === false ? this.copy.buyer_save : "Fortsæt";
        this.querySelector('[data-gift-product]').textContent = editing.contactOnly ? "Dine oplysninger holdes adskilt fra modtagerens leveringsadresse." : "Dette gælder de sæt, du selv modtager.";
        if (editing.contactOnly) for (const name of ['countryCode', 'address1', 'address2', 'city', 'province', 'postalCode']) {
          const field = this.form.elements[name]; field.required = false; field.closest('.dac-gifting__field').hidden = true;
        }
      }
      this.dialog.showModal();
      this.querySelector('.dac-gifting__dialog-body').scrollTop = 0;
      (isCart ? this.destination : this.form.elements.firstName).focus();
    }

    destinationChanged(notification) {
      const value = this.destination.value;
      this.form.hidden = value !== 'new';
      const summary = this.querySelector('[data-gift-saved-summary]');
      summary.hidden = !this.recipients.has(value);
      summary.textContent = this.recipients.has(value) ? addressText(this.recipients.get(value)) : '';
      if (value === 'new') this.provinces();
      const previous = [...this.allocations.values()].flat().find(part => part.destinationId === value && part.notification)?.notification;
      this.setNotification(notification || previous);
      this.quantityHint();
    }

    setNotification(notification) {
      if (!this.notificationsEnabled || !this.querySelector('[data-gift-notify]')) return;
      const person = this.destination.value === 'buyer' ? this.buyer : this.recipients.get(this.destination.value);
      this.querySelector('[data-gift-notify]').checked = Boolean(notification?.enabled);
      this.querySelector('[data-gift-notify-email]').value = notification?.email || this.notificationEmail;
      this.querySelector('[data-gift-notify-email]').readOnly = Boolean(this.notificationEmail);
      this.querySelector('[data-gift-notify-from]').value = notification?.from || this.buyer?.firstName || '';
      this.querySelector('[data-gift-notify-message]').value = notification?.message || '';
      this.notificationChanged();
    }

    notificationChanged() {
      const enabled = this.querySelector('[data-gift-notify]').checked;
      this.querySelector('[data-gift-notification-fields]').hidden = !enabled;
      this.querySelector('[data-gift-notify]').setAttribute('aria-expanded', String(enabled));
      for (const field of this.querySelectorAll('[data-gift-notification-fields] input')) field.required = enabled;
    }

    quantityHint() {
      if (!this.editing?.line) return;
      const remaining = Math.max(0, Number(this.quantity.max) - Number(this.quantity.value || 0));
      this.querySelector('[data-gift-quantity-hint]').textContent = remaining ? (remaining === 1 ? this.copy.remaining_one : this.copy.remaining_other.replace('{count}', remaining)) : this.copy.all_assigned;
    }

    provinces(selected = '') {
      const country = this.form.elements.countryCode.selectedOptions[0];
      const options = this.querySelector('[data-gift-countries]').content.querySelectorAll('option');
      const match = [...options].find(option => option.value === country?.dataset.name || option.textContent === country?.textContent);
      let provinces = [];
      try { provinces = JSON.parse(match?.dataset.provinces || '[]'); } catch { /* An empty list keeps the native field optional. */ }
      const select = this.form.elements.province;
      select.replaceChildren(new Option(country?.value === 'US' ? this.copy.select_state : country?.value === 'CA' ? this.copy.select_province : this.copy.select_region, ''));
      for (const [code, name] of provinces) select.add(new Option(name, code));
      this.querySelector('[data-gift-province-field]').hidden = provinces.length === 0;
      select.required = provinces.length > 0;
      const region = String(selected).trim().toLocaleLowerCase();
      const selectedProvince = provinces.find(([code, name]) => [code, name].some(value => String(value).toLocaleLowerCase() === region));
      select.value = selectedProvince?.[0] || '';
    }

    async save() {
      if (this.saving) return;
      if (this.serviceConfigured && !this.serviceReady) return this.showError({ code: 'GIFTING_UNAVAILABLE' });
      const isCart = Boolean(this.editing.line);
      if (isCart && !this.quantity.reportValidity()) return;
      if (!this.form.hidden && !this.form.reportValidity()) return;
      let notification;
      if (isCart && this.notificationsEnabled && this.querySelector('[data-gift-notify]')?.checked) {
        for (const field of this.querySelectorAll('[data-gift-notification-fields] input,[data-gift-notification-fields] textarea')) if (!field.reportValidity()) return;
        notification = { enabled: true, email: this.querySelector('[data-gift-notify-email]').value.trim(), from: this.querySelector('[data-gift-notify-from]').value.trim(), message: this.querySelector('[data-gift-notify-message]').value.trim() };
        if (!notification.from) { this.error.textContent = "Tilføj et navn så modtageren ved hvem gaven er fra."; this.querySelector('[data-gift-notify-from]').focus(); return; }
      }
      let destinationId = this.destination.value;
      if (!this.form.hidden) {
        const fields = Object.fromEntries(new FormData(this.form));
        if (!fields.firstName.trim() || !fields.lastName.trim() || (!this.editing.contactOnly && (!fields.address1.trim() || !fields.city.trim()))) {
          this.error.textContent = "Udfyld de obligatoriske adressefelter."; return;
        }
        destinationId = this.editing.recipientId || crypto.randomUUID();
        let person = {
          id: destinationId, label: fields.label.trim() || fields.firstName.trim(), relationship: fields.relationship,
          firstName: fields.firstName.trim(), lastName: fields.lastName.trim(), email: fields.email.trim(), phone: fields.phone.trim(),
          saved: !isCart || fields.save === 'on',
          address: { address1: fields.address1.trim(), address2: fields.address2.trim(), city: fields.city.trim(), province: fields.province, postalCode: fields.postalCode.trim(), countryCode: fields.countryCode, countryName: this.form.elements.countryCode.selectedOptions[0]?.textContent || '' },
        };
        if (this.editing.buyer) {
          if (this.editing.contactOnly) delete person.address;
          const previousBuyer = this.buyer, previousEdited = this.buyerEdited;
          this.buyer = person; this.buyerEdited = true;
          this.saving = true; this.querySelector('[data-gift-save]').disabled = true;
          try { await this.persistPlan(); }
          catch (error) { this.buyer = previousBuyer; this.buyerEdited = previousEdited; this.showError(error); return; }
          finally { this.saving = false; this.querySelector('[data-gift-save]').disabled = false; }
          this.dialog.close();
          this.renderCart();
          if (this.editing.resumeCheckout !== false) return this.beginCheckout();
          this.status.textContent = "Leveringsadressen er opdateret for denne indkøbskurv."; return;
        }
        if (this.serviceReady && person.saved) {
          if (!this.signedIn) { person.saved = false; }
          else {
            this.saving = true; this.querySelector('[data-gift-save]').disabled = true;
            try {
              const prior = this.recipients.get(this.editing.recipientId);
              person = { ...await this.api.saveRecipient(person, prior?.id, prior?.version), saved: true };
              destinationId = person.id;
            } catch (error) { return this.showError(error); }
            finally { this.saving = false; this.querySelector('[data-gift-save]').disabled = false; }
          }
        }
        this.recipients.set(destinationId, person);
      }
      const priorAllocations = new Map([...this.allocations].map(([key, parts]) => [key, parts.map(part => ({ ...part }))]));
      if (isCart) {
        const key = this.editing.line.dataset.lineKey;
        const parts = this.allocations.get(key) || [];
        const allocation = { quantity: Number(this.quantity.value), destinationId, gift: true, ...(notification ? { notification } : {}) };
        if (this.editing.part == null) parts.push(allocation); else parts[this.editing.part] = allocation;
        this.allocations.set(key, parts);
        // One message per recipient shipment, even when it contains several kits.
        for (const current of [...this.allocations.values()].flat()) if (current.destinationId === destinationId) {
          if (notification) current.notification = { ...notification }; else delete current.notification;
        }
      }
      if (isCart) {
        this.saving = true; this.querySelector('[data-gift-save]').disabled = true;
        try { await this.persistPlan(); } catch (error) { this.allocations = priorAllocations; this.showError(error); return; }
        finally { this.saving = false; this.querySelector('[data-gift-save]').disabled = false; }
      }
      this.dialog.close();
      this.renderCart(); this.renderRecipients();
      if (isCart) {
        const parts = this.allocations.get(this.editing.line.dataset.lineKey) || [];
        const index = this.editing.part == null ? parts.length - 1 : this.editing.part;
        this.opener = this.editing.line.querySelector(`[data-part="${index}"]`) || this.editing.line.querySelector('[data-gift-edit]');
        this.opener?.focus();
      } else this.focusRecipient(destinationId);
      this.status.textContent = this.serviceReady ? (isCart ? "Gaveoplysningerne er gemt." : "Modtageren er gemt på din konto.") : (isCart ? "Gaveoplysningerne er tilføjet i denne forhåndsvisning." : "Modtageren er gemt i denne forhåndsvisning. Lagring på kontoen bliver tilgængelig, når gavefunktionen er tilsluttet.");
    }

    renderCart(reconcile = false) {
      if (this.dataset.mode !== 'cart') return;
      const lines = [...document.querySelectorAll('[data-gift-line]')];
      const keys = new Set(lines.map(line => line.dataset.lineKey));
      let changed = false;
      for (const key of this.allocations.keys()) if (!keys.has(key)) { this.allocations.delete(key); changed = true; }
      const deliveries = new Map();
      for (const line of lines) {
        const key = line.dataset.lineKey;
        let parts = this.allocations.get(key) || [];
        const quantity = Number(line.dataset.quantity);
        if (parts.reduce((sum, part) => sum + part.quantity, 0) > quantity) {
          this.allocations.delete(key); parts = []; changed = true;
        }
        const button = line.querySelector('[data-gift-edit]');
        const allocated = parts.reduce((sum, part) => sum + part.quantity, 0);
        button.hidden = allocated >= quantity;
        button.disabled = Boolean(this.connecting);
        line.querySelector('[data-gift-label]').textContent = parts.length ? "Send endnu en som gave" : "Send som gave";
        const summary = line.querySelector('[data-gift-summary]');
        summary.replaceChildren();
        parts.forEach((part, index) => {
          const person = this.recipients.get(part.destinationId);
          const label = part.destinationId === 'buyer' ? "Min adresse ved betaling" : person?.label || "Modtager";
          const edit = node('button', `${part.quantity} gift${part.quantity === 1 ? '' : 's'} → ${label} · Edit`, 'dac-gifting__text');
          edit.type = 'button'; edit.dataset.giftEdit = ''; edit.dataset.part = index;
          summary.append(edit);
          deliveries.set(part.destinationId, (deliveries.get(part.destinationId) || 0) + part.quantity);
        });
        if (quantity > allocated) deliveries.set('buyer', (deliveries.get('buyer') || 0) + quantity - allocated);
      }
      const panel = this.querySelector('[data-gift-deliveries]');
      const hasGifts = this.hasGifts();
      panel.hidden = !hasGifts;
      this.toggleAttribute('data-gift-checkout', hasGifts);
      this.toggleAttribute('data-gift-connecting', Boolean(this.connecting));
      for (const summary of document.querySelectorAll('.js-cart-summary__info')) {
        let note = summary.querySelector('.dac-gifting__shipping-note');
        if (hasGifts && !note) {
          note = node('p', panel.querySelector('p').textContent, 'dac-gifting__shipping-note');
          summary.prepend(note);
        }
        if (note) note.hidden = !hasGifts;
      }
      const list = this.querySelector('[data-gift-delivery-list]');
      list.replaceChildren();
      for (const [destinationId, quantity] of deliveries) {
        const label = destinationId === 'buyer' ? "Min adresse ved betaling" : this.recipients.get(destinationId)?.label || "Modtager";
        const row = node('div', null, 'dac-gifting__delivery');
        const copy = node('div'); copy.append(node('strong', label));
        const person = destinationId === 'buyer' ? this.buyer : this.recipients.get(destinationId);
        if (person?.address) copy.append(node('span', [person.address.address1, person.address.city].filter(Boolean).join(', '), 'dac-gifting__delivery-address'));
        row.append(copy, node('span', `${quantity} item${quantity === 1 ? '' : 's'}`, 'dac-gifting__delivery-count')); list.append(row);
      }
      if (reconcile && changed) this.status.textContent = "Din indkøbskurv er ændret. Vælg gaveoplysningerne igen for de ændrede varer.";
    }

    focusRecipient(id) {
      this.opener = [...this.querySelectorAll('[data-recipient-edit]')].find(button => button.dataset.recipientEdit === id);
      this.opener?.focus();
    }

    renderRecipients() {
      const list = this.querySelector('[data-recipient-list]');
      if (!list) return;
      list.replaceChildren();
      const add = this.querySelector('[data-recipient-add]');
      if (add) add.disabled = this.serviceConfigured && !this.serviceReady;
      if (this.serviceConfigured && !this.serviceReady) return;
      const saved = [...this.recipients.values()].filter(person => person.saved);
      const count = this.querySelector('[data-recipient-count]');
      if (count) count.textContent = saved.length ? (saved.length === 1 ? this.copy.count_one : this.copy.count_other.replace('{count}', saved.length)) : '';
      if (!saved.length) {
        const empty = node('div', null, 'dac-gifting__empty');
        empty.append(node('h2', 'Your next thoughtful gift starts here.'), node('p', 'Add a friend or family member once, then choose them whenever you send a gift.'));
        list.append(empty); return;
      }
      for (const person of saved) {
        const card = node('article', null, 'dac-gifting__recipient');
        const heading = node('div', null, 'dac-gifting__recipient-heading');
        heading.append(node('h3', person.label), node('span', labels[person.relationship], 'dac-gifting__badge'));
        card.append(heading, node('address', addressText(person)));
        const actions = node('div', null, 'dac-gifting__recipient-actions');
        const edit = node('button', 'Edit', 'dac-gifting__text'); edit.type = 'button'; edit.dataset.recipientEdit = person.id; edit.setAttribute('aria-label', `Rediger ${person.label}`);
        const remove = node('button', 'Remove', 'dac-gifting__text'); remove.type = 'button'; remove.dataset.recipientRemove = person.id; remove.setAttribute('aria-label', `Fjern ${person.label}`);
        actions.append(edit, remove); card.append(actions); list.append(card);
      }
    }

    hasGifts() { return [...this.allocations.values()].some(parts => parts.length); }

    async connectService() {
      this.connecting = true; this.renderRecipients(); this.renderCart();
      try {
        if (!window.DacGiftApi) throw new Error();
        this.api = new window.DacGiftApi(this.dataset.apiBase);
        const session = await this.api.session();
        this.signedIn = session.signedIn; this.buyer = session.buyer;
        this.notificationEmail = this.querySelector('[data-gift-notification]') ? session.notificationEmail || '' : '';
        this.notificationsEnabled = Boolean(this.querySelector('[data-gift-notification]') && session.notificationsEnabled);
        for (const person of session.recipients) this.recipients.set(person.id, { ...person, saved: true });
        if (this.dataset.mode === 'cart' && this.signedIn) await this.restorePlan();
        this.serviceReady = true; this.connecting = false;
        if (this.status.textContent === this.copy.loading) this.status.textContent = '';
        this.querySelector('.dac-gifting__preview').hidden = true;
        this.form.elements.save.checked = this.signedIn; this.form.elements.save.defaultChecked = this.signedIn;
        this.querySelector('[data-gift-buyer-edit]')?.removeAttribute('hidden');
        this.form.elements.save.disabled = !this.signedIn;
        this.querySelector('[data-gift-save-label] span').textContent = this.signedIn ? "Gem denne modtager på min konto" : "Log ind for at gemme modtagere til næste gang";
        this.renderRecipients(); this.renderCart();
      } catch (error) {
        this.connecting = false;
        this.querySelector('.dac-gifting__preview').hidden = true;
        this.renderCart(); this.renderRecipients(); this.showError(error);
      }
    }

    showError(error) {
      const messages = {
        INVALID_EMAIL: 'Enter a valid recipient email address.',
        INVALID_GIFT_MESSAGE: 'Keep your personal note to 250 characters.',
        INVALID_GIFT_NOTIFICATION: 'Check the recipient email and sender name.',
        GIFT_NOTIFICATION_CONFLICT: 'Choose the same email and note for gifts going to the same recipient.',
        GIFT_PLAN_CHANGED: 'Gift details changed in another window. Refresh to load the latest choices.',
        GIFT_UNAVAILABLE: 'We can’t complete this gift order. Please contact Customer Care.',
        SIGN_IN_REQUIRED: 'Please sign in to save recipients to your account.',
        RECIPIENT_CHANGED: 'This recipient was updated in another window. Refresh before saving again.',
        ADDRESS_NOT_SHIPPABLE: 'Delivery isn’t available for one of these addresses. Please check the address.',
        CART_CHANGED: 'Your cart changed. Please review your gift details again.',
        CART_PRICE_CHANGED: 'Your cart price changed. Please refresh your cart before continuing.',
        QUOTE_EXPIRED: 'Your delivery quote expired. Please review shipping again.',
        DELIVERY_CHANGED: 'Shipping or taxes changed. Please review the updated total.',
        CHECKOUT_PENDING: 'We’re checking your checkout request. Please don’t submit another one.',
        CHECKOUT_ALREADY_COMPLETED: this.copy.checkout_paid,
        REQUEST_UNCERTAIN: 'We couldn’t confirm that request. Refresh to check before trying again.',
        DISCOUNT_INTEGRATION_REQUIRED: 'Gift checkout isn’t available for this discounted cart yet. Your cart has been kept.',
        CURRENCY_INTEGRATION_REQUIRED: 'Gift checkout isn’t available in this currency yet. Your cart has been kept.',
        RESTRICTIONS_UNAVAILABLE: 'We can’t verify this gift order right now. Please try again later.',
      };
      const message = messages[error?.code] || "Gavefunktionen er midlertidigt utilgængelig. Din indkøbskurv er bevaret.";
      (this.dialog.open ? this.error : this.status).textContent = message;
    }

    async currentCart() {
      const response = await fetch(`${this.dataset.cartUrl}.js`, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15_000) });
      if (!response.ok) throw new Error();
      return response.json();
    }

    async restorePlan() {
      const cart = await this.currentCart();
      const result = await this.api.cartPlan(cart);
      this.planVersion = result.version;
      if (!result.plan || result.plan.fingerprint !== this.cartFingerprint(cart)) return;
      const plan = result.plan;
      for (const person of plan.destinations) if (!this.recipients.has(person.id)) this.recipients.set(person.id, { ...person, saved: false });
      this.allocations.clear();
      for (const { lineKey, notification, ...part } of plan.allocations) {
        if (this.notificationsEnabled && (!this.notificationEmail || notification?.email === this.notificationEmail)) part.notification = notification;
        const parts = this.allocations.get(lineKey) || []; parts.push(part); this.allocations.set(lineKey, parts);
      }
      if (plan.buyer) { this.buyer = { ...this.buyer, ...plan.buyer }; this.buyerEdited = true; }
    }

    persistPlan() {
      if (!this.serviceReady || !this.signedIn || this.dataset.mode !== 'cart') return Promise.resolve();
      const save = async () => {
        const cart = await this.currentCart();
        const allocations = [...this.allocations].flatMap(([lineKey, parts]) => parts.map(part => ({ ...part, lineKey })));
        const used = new Set(allocations.map(part => part.destinationId));
        const plan = { fingerprint: this.cartFingerprint(cart), allocations,
          destinations: [...this.recipients.values()].filter(person => used.has(person.id)),
          buyer: this.buyerEdited ? this.buyer : null };
        const result = await this.api.cartPlan(cart, this.planVersion || 0, plan);
        this.planVersion = result.version;
      };
      this.planWrite = (this.planWrite || Promise.resolve()).catch(() => {}).then(save);
      return this.planWrite;
    }

    cartFingerprint(cart) { return JSON.stringify(cart.items.map(item => [item.key, item.quantity, item.final_line_price, item.properties])); }

    async beginCheckout() {
      if (this.checkingOut) return;
      if (!this.serviceReady) return this.showError({ code: 'GIFTING_UNAVAILABLE' });
      this.checkingOut = true;
      try {
        this.status.textContent = "Tjekker din indkøbskurv…";
        await this.planWrite;
        const cart = await this.currentCart(), allocations = [];
        if (this.pendingCheckout?.fingerprint === this.cartFingerprint(cart) && this.pendingCheckout.quote.expiresAt > Date.now()) {
          const { quote, details } = this.pendingCheckout;
          this.reviewFingerprint = this.cartFingerprint(cart); this.showReview(quote, cart, details);
          this.status.textContent = ''; return;
        }
        this.pendingCheckout = null;
        for (const item of cart.items) {
          const parts = this.allocations.get(item.key) || [];
          parts.forEach(part => allocations.push({ ...part, lineKey: item.key }));
          const remainder = item.quantity - parts.reduce((sum, part) => sum + part.quantity, 0);
          if (remainder < 0) throw { code: 'CART_CHANGED' };
          if (remainder) allocations.push({ lineKey: item.key, quantity: remainder, destinationId: 'buyer', gift: false });
        }
        const needsBuyerAddress = allocations.some(part => part.destinationId === 'buyer');
        if (!this.buyer?.firstName || !this.buyer?.lastName || (!this.signedIn && !this.buyer?.email) || (needsBuyerAddress && !this.buyer?.address?.address1)) {
          this.open({ buyer: true, contactOnly: !needsBuyerAddress }, this.opener); return;
        }
        const used = new Set(allocations.map(part => part.destinationId));
        const destinations = [...this.recipients.values()].filter(person => used.has(person.id));
        this.status.textContent = "Beregner fragt for hver adresse…";
        const details = { allocations, destinations, buyer: this.buyer };
        const quote = await this.api.quote(cart, details);
        this.reviewFingerprint = this.cartFingerprint(cart);
        this.showReview(quote, cart, details);
        this.status.textContent = '';
      } catch (error) { this.showError(error); }
      finally { this.checkingOut = false; }
    }

    showReview(quote, cart, details) {
      if (!this.reviewDialog) {
        this.reviewDialog = node('dialog', null, 'dac-gifting__dialog dac-gifting__review');
        this.reviewDialog.setAttribute('aria-labelledby', 'gift-review-title');
        this.reviewDialog.addEventListener('close', () => { this.checkoutWatch?.abort(); this.opener?.focus(); });
        this.append(this.reviewDialog);
      }
      this.checkoutWatch?.abort();
      const revision = this.reviewVersion = (this.reviewVersion || 0) + 1;
      const dialog = this.reviewDialog;
      dialog.replaceChildren();
      const heading = node('h2', 'Review your deliveries'); heading.id = 'gift-review-title';
      const header = node('div', null, 'dac-gifting__dialog-heading');
      const close = this.querySelector('[data-gift-close]').cloneNode(true);
      close.removeAttribute('data-gift-close'); close.addEventListener('click', () => dialog.close());
      header.append(heading, close); dialog.append(header);
      const body = node('div', null, 'dac-gifting__dialog-body'); dialog.append(body);
      const money = value => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: quote.total.currency }).format(Number(value));
      for (const shipment of quote.shipments) {
        const row = node('section', null, 'dac-gifting__shipment');
        row.append(node('h3', shipment.label));
        const destinationId = shipment.destinationIds[0];
        const person = destinationId === 'buyer' ? details.buyer : details.destinations.find(value => value.id === destinationId);
        const items = node('ul', null, 'dac-gifting__shipment-items');
        for (const part of details.allocations.filter(part => shipment.destinationIds.includes(part.destinationId))) {
          const item = cart.items.find(item => item.key === part.lineKey);
          if (item) items.append(node('li', `${item.product_title || item.title || 'Kit'} × ${part.quantity}`));
        }
        if (items.childElementCount) row.append(items);
        if (person?.address) row.append(node('address', addressText(person), 'dac-gifting__address-summary'));
        const select = node('select', null, 'dac-gifting__shipping-select'); select.setAttribute('aria-label', `Fragt til ${shipment.label}`);
        for (const rate of shipment.availableRates) select.add(new Option(`${rate.title} · ${money(rate.amount)}`, rate.handle, false, rate.handle === shipment.selectedRate.handle));
        select.addEventListener('change', async () => {
          dialog.querySelectorAll('select,.dac-gifting__primary').forEach(control => { control.disabled = true; });
          progress.textContent = this.copy.loading;
          try {
            const deliverySelections = Object.fromEntries(quote.shipments.map(s => [s.id, s.id === shipment.id ? select.value : s.selectedRate.handle]));
            const updated = await this.api.quote(cart, { ...details, deliverySelections });
            if (dialog.open && this.reviewVersion === revision) this.showReview(updated, cart, { ...details, deliverySelections });
          } catch (error) { if (dialog.open && this.reviewVersion === revision) { dialog.close(); this.showError(error); } }
        });
        const notification = details.allocations.find(part => shipment.destinationIds.includes(part.destinationId) && part.notification)?.notification;
        if (notification) {
          const email = node('div', null, 'dac-gifting__email-summary');
          email.append(node('strong', 'A surprise in their inbox'), node('p', `Email ${notification.email} when this gift ships.`), node('p', `From ${notification.from}`));
          if (notification.message) email.append(node('blockquote', notification.message));
          row.append(email);
        }
        row.append(select, node('p', `Items ${money(shipment.subtotal)} · ${shipment.taxesIncluded ? 'Included tax' : 'Tax'} ${money(shipment.tax)}`, 'dac-gifting__shipment-cost')); body.append(row);
      }
      const footer = node('div', null, 'dac-gifting__dialog-footer');
      const total = node('p', null, 'dac-gifting__total'); total.append(node('span', 'Total'), node('strong', money(quote.total.amount))); footer.append(total); dialog.append(footer);
      const actions = node('div', null, 'dac-gifting__actions');
      const back = node('button', 'Back to cart', 'dac-gifting__secondary'); back.type = 'button'; back.addEventListener('click', () => dialog.close());
      const pending = this.pendingCheckout?.quote.id === quote.id;
      const progress = node('p', pending ? this.copy.checkout_preparing : '', 'dac-gifting__status');
      progress.setAttribute('role', 'status'); progress.setAttribute('aria-live', 'polite'); footer.prepend(progress);
      const proceed = node('button', pending ? this.copy.check_checkout : 'Continue to payment', 'dac-gifting__primary'); proceed.type = 'button';
      if (pending) dialog.querySelectorAll('select').forEach(control => { control.disabled = true; });
      proceed.addEventListener('click', async () => {
        if (proceed.disabled) return;
        this.checkoutWatch?.abort();
        const watch = new AbortController(); this.checkoutWatch = watch;
        const active = () => !watch.signal.aborted && dialog.open;
        proceed.disabled = true;
        dialog.querySelectorAll('select').forEach(control => { control.disabled = true; });
        progress.textContent = this.copy.checkout_preparing;
        const verifyCart = async () => {
          const response = await fetch(`${this.dataset.cartUrl}.js`, { credentials: 'same-origin', cache: 'no-store' });
          if (!response.ok || this.cartFingerprint(await response.json()) !== this.reviewFingerprint) throw { code: 'CART_CHANGED' };
        };
        try {
          await verifyCart();
          if (!active()) return;
          let result;
          if (this.pendingCheckout?.quote.id !== quote.id) {
            this.pendingCheckout = { quote, cart, details, fingerprint: this.reviewFingerprint };
            try { result = await this.api.checkout(quote.id); }
            catch (error) { if (!['CHECKOUT_PENDING', 'REQUEST_UNCERTAIN'].includes(error.code)) throw error; }
          }
          if (!result) {
            progress.textContent = this.copy.checkout_waiting;
            for (const delay of [0, 2000, 5000, 10000, 15000, 20000, 30000]) {
              if (!active()) return;
              if (delay) await new Promise(resolve => {
                const finish = () => { clearTimeout(timer); watch.signal.removeEventListener('abort', finish); resolve(); };
                const timer = setTimeout(finish, delay); watch.signal.addEventListener('abort', finish, { once: true });
              });
              if (!active()) return;
              try { result = await this.api.checkoutStatus(quote.id); break; }
              catch (error) { if (!['CHECKOUT_PENDING', 'REQUEST_UNCERTAIN', 'GIFTING_UNAVAILABLE', 'TRY_AGAIN_LATER'].includes(error.code)) throw error; }
            }
          }
          if (!active()) return;
          if (!result) {
            progress.textContent = this.copy.checkout_pending;
            proceed.textContent = this.copy.check_checkout; return;
          }
          await verifyCart();
          if (!active()) return;
          const url = new URL(result.url);
          if (url.protocol !== 'https:' || ![this.dataset.checkoutHost, window.location.hostname].includes(url.hostname) || url.username || url.password || url.port) throw new Error();
          if (result.totalChanged) {
            if (result.total?.currency !== quote.total.currency || !/^\d+(\.\d{1,2})?$/.test(result.total.amount)) throw new Error();
            heading.textContent = "Dit endelige totalbeløb ved betaling";
            progress.textContent = "Fragt og afgifter er færdigberegnet for dine leveringer. Tjek dit totalbeløb, før du betaler.";
            // The provider returns a final combined tax, not an address-level
            // breakdown. Remove the earlier estimates so visible rows add up.
            const subtotalCents = quote.shipments.reduce((sum, shipment) => sum + Math.round(Number(shipment.subtotal) * 100), 0);
            const shippingCents = quote.shipments.reduce((sum, shipment) => sum + Math.round(Number(shipment.availableRates.find(rate => rate.handle === shipment.selectedRate.handle)?.amount) * 100), 0);
            const taxCents = Math.round(Number(result.total.amount) * 100) - subtotalCents - shippingCents;
            if (!Number.isSafeInteger(taxCents) || taxCents < 0) throw new Error();
            body.querySelectorAll('.dac-gifting__shipment-cost').forEach((cost, index) => { cost.textContent = `Varer ${money(quote.shipments[index].subtotal)}`; });
            progress.append(node('br'), node('span', `Items ${money(subtotalCents / 100)} · Shipping ${money(shippingCents / 100)} · Tax ${money(taxCents / 100)}`));
            total.querySelector('strong').textContent = money(result.total.amount);
            const pay = node('a', 'Continue to payment', 'dac-gifting__primary'); pay.href = url.href;
            proceed.replaceWith(pay); pay.focus(); return;
          }
          window.location.assign(url.href);
        } catch (error) {
          if (!active()) return;
          if (['CART_CHANGED', 'QUOTE_EXPIRED', 'CHECKOUT_ALREADY_COMPLETED'].includes(error.code)) this.pendingCheckout = null;
          dialog.close(); this.showError(error);
        } finally { if (active()) proceed.disabled = false; }
      });
      actions.append(back, proceed); footer.append(actions);
      if (!dialog.open) dialog.showModal();
      proceed.focus();
    }

    checkout(event) {
      if (this.dataset.mode !== 'cart') return;
      if (event.type === 'submit' && !event.target.matches('.checkout-button,form[action*="/cart"]')) return;
      if (this.connecting) {
        event.preventDefault(); event.stopImmediatePropagation();
        this.status.textContent = this.copy.loading; return;
      }
      if (!this.hasGifts()) return;
      event.preventDefault(); event.stopImmediatePropagation();
      if (this.serviceConfigured) { this.opener = event.submitter || event.target.closest('button,a,input') || this.opener; this.beginCheckout(); return; }
      this.status.textContent = "Dette er en forhåndsvisning af gavefunktionen. Betaling for gaver er endnu ikke tilsluttet, så ingen ordre er afgivet. Fjern gaveoplysningerne for at bruge den eksisterende betalingsside.";
      this.status.tabIndex = -1; this.status.focus();
      this.status.scrollIntoView({ block: 'center', behavior: 'instant' });
    }
  }
  customElements.define('dac-gifting', Gifting);
})();
